import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import DetailsCartRow from "./DetailsCartRow";
import EditCartForm from "./EditCartForm";
import ReceivedProduct from "../Product/ReceivedProduct";
import {
  updateCart,
  updateCartWithImage,
} from "../../../../redux/reducer/cartReducer";

const DetailsCartTable = ({
  details,
  onDelete,
  onUpdate,
  isEditing,
  setEditingOrder,
  isProducts,
  setEditingProducts,
  loading,
  refreshData,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State for price form and edit form
  const [show, setShow] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // States for the Edit Modal
  const [editTotalPackingFees, setEditTotalPackingFees] = useState("");
  const [selectedEditImage, setSelectedEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(details.img || null);
  const [totalWeightAfterPacking, setTotalWeightAfterPacking] = useState(
    details.totalWeightAfterPacking || null
  );

  const [editForm, setEditForm] = useState({
    image: details.img || "",
    totalPackingFees: "",
    totalWeightAfterPacking: details.totalWeightAfterPacking || "",
  });

  // Function to handle image selection in Edit
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedEditImage(file);
      // Create a preview for the new image
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to submit the edit
  const handleEditSubmit = async () => {
    try {
      if (editTotalPackingFees !== "" || totalWeightAfterPacking !== "") {
        await dispatch(
          updateCart({
            cartId: details.id,
            data: {
              totalPackingFees: editTotalPackingFees,
              totalWeightAfterPacking: totalWeightAfterPacking,
            },
          })
        ).unwrap();
      }

      // Upload the new image if selected
      if (selectedEditImage) {
        await dispatch(
          updateCartWithImage({
            cartId: details.id,
            image: selectedEditImage,
          })
        ).unwrap();
      }

      // Close the modal and update the data
      setShowEditModal(false);
      refreshData();
    } catch (error) {
      alert(error || "Failed to update cart.");
    }
  };

  // Close the modal and clear the data
  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditTotalPackingFees("");
    setSelectedEditImage(null);
    setEditImagePreview(details.img || null);
  };

  const handleDetails = () => {
    setShow(true);
    setEditingProducts(details.id);
  };

  const handleEdit = () => {
    // Initialize the modal's initial data
    setEditTotalPackingFees("");
    setEditImagePreview(details.img || null);
    setSelectedEditImage(null);
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmitOld = (e) => {
    e.preventDefault();
    const updatedData = {
      size: editForm.Size,
      productCount: editForm.Count,
    };
    dispatch(updateCart({ id: details.id, data: updatedData }));
    setEditingOrder(null);
    if (onUpdate) onUpdate(updatedData);
  };

  return (
    <>
      <DetailsCartRow
        details={details}
        onDelete={onDelete}
        onEdit={handleEdit}
        onDetails={handleDetails}
        pdf={details.paymentPDF}
      />

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-lg font-bold text-black">
              {t("Edit Package Info")}
            </h2>

            {/* Add weight field after packaging */}
            <label className="block mb-2 text-sm font-medium text-gray-700">
              {t("Weight After Packing")}
            </label>
            <input
              type="number"
              value={totalWeightAfterPacking}
              onChange={(e) => setTotalWeightAfterPacking(e.target.value)}
              className="w-full p-2 mb-4 text-black bg-gray-300 rounded"
              placeholder={t("Enter Weight After Packing (KG)")}
            />

            {/* Cost edit field */}

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                {t("Total Packing Fees")}
              </label>
              <input
                type="number"
                value={editTotalPackingFees}
                onChange={(e) => setEditTotalPackingFees(e.target.value)}
                className="w-full p-2 text-black bg-gray-300 rounded"
                placeholder={t("Enter packing fees")}
              />
            </div>

            {/* Image edit field*/}
            <div className="mb-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleEditImageChange}
                className="w-full p-2 text-black bg-gray-300 rounded file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              {selectedEditImage && (
                <p className="mt-2 text-sm text-gray-600">
                  {t("New image")}: {selectedEditImage.name}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleEditCancel}
                className="px-4 py-2 text-black bg-gray-300 rounded hover:bg-gray-400"
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleEditSubmit}
                className="px-4 py-2 font-semibold text-black rounded bg-custom-yellow hover:bg-yellow-600"
              >
                {t("Update")}
              </button>
            </div>
          </div>
        </div>
      )}

      {(isEditing || isProducts) && (
        <tr>
          <td colSpan="100%">
            {isEditing ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                <EditCartForm
                  editForm={editForm}
                  onChange={handleEditChange}
                  onSubmit={handleEditSubmitOld}
                  onCancel={() => setEditingOrder(null)}
                />
              </div>
            ) : (
              <div
                className={`fixed inset-0 min-h-full z-50 overflow-y-auto bg-yellow-50 transform transition-transform duration-700 ease-out ${
                  show
                    ? "translate-x-0 duration-700"
                    : "translate-x-full duration-700"
                }`}
              >
                <div className="mt-5 md:mx-5 md:mt-10">
                  <ReceivedProduct
                    data={details.products}
                    cartID={details.id}
                    setEditingProducts={setEditingProducts}
                    refreshData={refreshData}
                    loading={loading}
                    cartIsPacking={details.isPacking}
                    cartIsOutCart={details.isOutCart}
                    cartIsReadyToPaid={details.isReadyToPaid}
                    cartStatus={details.status}
                    cart={details}
                  />
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export default DetailsCartTable;
